<template>
    <modal width="96%" :maxWidth="booking? '640px' : '240px'" @close="$emit('close')" style="font-size: 16px;">
        <div slot="title">{{$t('customer_booking_checker.check_booking')}}</div>
        <div class="modal-content-wrapper container py-2">
            <div v-if="recentPaidPayment">
                <div class="alert alert-success d-flex">
                    <div class="flex-grow-0">
                        {{ $t('you_just_paid')}}
                    </div>
                    <div class="flex-grow-1 text-right">
                        <div class="payment-item-title">{{recentPaidPayment.item_title}} {{ getExchangedAmount(recentPaidPayment.amount, recentPaidPayment.settled_currency) }}</div>
                    </div>
                </div>
            </div>
            <div v-if="acceptingPayment && (hasExpiredPayments || hasPendingPayments || hasCancelledPayments)" class="pr-2">
                <div>
                    <label class="field-label">{{$t('payment_requests')}}</label>
                </div>
                <div v-for="p in booking.pending_payments" :key="p.id" class="alert alert-primary d-flex">
                    <div class="flex-grow-1">
                        <div class="payment-item-title">
                            <span class="icon-payment"></span>{{p.item_title}} {{ getExchangedAmount(p.amount, p.settled_currency) }}
                        </div>
                        <div v-if="p.gateway_name === 'atm'">
                            <small>{{ $t('gateway.atm_info') }}</small>
                            <div class="white-space-pre-wrap">{{provider.atm_bank_info}}</div>
                            <button class="btn btn-sm btn-success" @click="paymentToShowAtmReportModal = p">{{$t('report_bank_transfer')}}</button>
                            <atm-report-modal
                                v-if="paymentToShowAtmReportModal" :payment="p"
                                @close="paymentToShowAtmReportModal = null"
                                @reported="(report) => onPaymentATMReported(paymentToShowAtmReportModal, report)" />
                            <div v-if="p.atm_payment">
                                <hr/>
                                <div class="atm-report-info">{{$t('reported_last_5_digits')}} {{p.atm_payment.account_last_five_digits}}</div>
                                <div class="atm-report-info">{{$t('transferred_at')}} {{formatedTransferredAt(p.atm_payment)}}</div>
                                <div class="atm-report-info" v-if="p.atm_payment.image_path">
                                    <a target="_blank" :href="context.storageEndpoint + p.atm_payment.image_path">{{$t('check_atm_report_photo')}}</a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="flex-grow-0">
                        <a v-if="p.gateway_name && (p.gateway_name !== 'manual' && p.gateway_name !== 'atm')" :href="p.redirectUrl" class="text-info">{{$t('pay')}}</a>
                        <div v-else>
                            <a v-for="option in p.gatewayOptions" :key="option.name" :href="option.redirectUrl" class="btn btn-sm btn-primary mx-1">{{ option.name }}</a>
                        </div>
                        <div v-if="(p.gateway_name !== 'manual') && paymentValidThrough(p)" class="text-right mx-2">
                            <small>{{ $t('payment_valid_through', { validThrough: paymentValidThrough(p)}) }}</small>
                        </div>
                        <div v-if="p.gateway_name === 'manual'"><small>{{ $t('manual_collect') }}</small></div>
                    </div>
                </div>
                <div v-for="p in booking.expired_payments" :key="p.id" class="alert alert-primary d-flex">
                    <div class="flex-grow-1">
                        <div class="payment-item-title"><span class="icon-payment"></span>{{p.item_title}} {{getExchangedAmount(p.amount, p.settled_currency)}}</div>
                    </div>
                    <div class="flex-grow-0">
                        {{ $t('expired')}}
                    </div>
                </div>
                <div v-for="p in booking.cancelled_payments" :key="p.id" class="alert alert-primary d-flex">
                    <div class="flex-grow-1">
                        <div class="payment-item-title"><span class="icon-payment"></span>{{p.item_title}} {{getExchangedAmount(p.amount, p.settled_currency)}}</div>
                    </div>
                    <div class="flex-grow-0">
                        {{ $t('cancelled')}}
                    </div>
                </div>
            </div>
            <div v-if="!booking">
                <div v-if="error" class="alert alert-danger">{{error}}</div>

                <input type="text" class="form-control" :placeholder="$t('customer_booking_checker.enter_your_booking_id')" v-model="displayIdToCheck" />
                <button class="btn btn-primary btn-sm btn-block mt-2 mb-2" @click="getBooking()">{{$t('customer_booking_checker.query')}}</button>
            </div>
            <div v-else>
                <div class="row">
                    <div class="col-12 col-md-6">
                        <label class="field-label">{{$t('provider.booking_status')}}</label>
                        <div class="field-value" :class="{'text-danger': (booking.status === 'cancelled' || booking.status === 'rejected')}">
                            <span v-if="isBookingOverdue(booking) && booking.status.toLowerCase() === 'unapproved'">
                                {{$t('customer_booking_checker.expired')}}
                            </span>
                            <span v-else>
                                {{$t('customer_booking_checker.booking_status_' + booking.status)}}
                            </span>

                            <span v-if="booking.reject_reason">({{$t('customer_booking_checker.reason')}}{{booking.reject_reason}})</span>
                        </div>
                    </div>
                    <div class="col-12 col-md-6">
                        <label class="field-label">{{$t('booking.display_id')}}</label>
                        <div class="field-value">{{booking.display_id}}</div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-12 col-md-6">
                        <label class="field-label">{{provider.landing_service_label || $t('booking.service_information')}}</label>
                        <div class="field-value">{{booking.service.title}}</div>
                    </div>
                    <div class="col-12 col-md-6">
                        <label class="field-label">{{provider.landing_member_label || $t('provider.member_user')}}</label>
                        <div class="field-value">{{booking.member_user.name}}</div>
                    </div>
                    <div v-if="!booking.service.allow_custom_duration" class="col-12 col-md-6">
                        <label class="field-label">{{provider.landing_choose_time_label || $t('booking.datetime')}}</label>
                        <div class="field-value">{{booking.display_datetime}}</div>
                    </div>
                    <div v-if="!booking.service.allow_custom_duration" class="col-12 col-md-6">
                        <label class="field-label">{{$t('booking.service_duration')}}</label>
                        <div class="field-value">{{booking.display_duration}}</div>
                    </div>
                    <div v-if="booking.service.allow_custom_duration" class="col-12 col-md-6">
                        <label class="field-label">{{provider.landing_choose_time_label || $t('booking.datetime')}}</label>
                        <div class="field-value">{{booking.display_datetime}}</div>
                    </div>
                    <div v-if="booking.service.allow_custom_duration" class="col-12 col-md-6">
                        <label class="field-label">{{provider.landing_choose_end_time_label || $t('booking.end_datetime')}}</label>
                        <div class="field-value">{{booking.display_end_datetime}}</div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-12 col-md-6">
                        <label class="field-label">{{$t('booking.customer_information')}}</label>
                        <div class="field-value" v-html="customerInformation"></div>
                    </div>
                    <div class="col-12 col-md-6" v-if="booking.note_to_customer">
                        <label class="field-label">{{$t('booking.note_from_provider')}}</label>
                        <div class="field-value">{{booking.note_to_customer}}</div>
                    </div>
                </div>
                <div v-if="booking && booking.paid_payments && booking.paid_payments.length > 0">
                    <div>
                        <label class="field-label">{{$t('paid_payments')}}</label>
                    </div>
                    <div class="mt-2 mb-4">
                        <div v-for="p in booking.paid_payments" :key="p.id" class="mt-2">
                            <div class="payment-item-title"><span class="icon-payment"></span>{{getExchangedAmount(p.amount, p.settled_currency)}} {{p.item_title}} </div>
                        </div>
                    </div>
                </div>
                <div v-if="booking && booking.order">
                    <div>
                        <label class="field-label">{{$t('order')}}</label>
                    </div>
                    <div class="mt-2 mb-4">
                        <div v-if="nonEmptyOrderItems.length > 0">
                            <div v-for="item in nonEmptyOrderItems" :key="item.id" class="d-flex">
                                <div>{{ item.title === 'points_deduction' ? $t('points_deduction', {points: item.points_deducted}) : item.title }}
                                    <div class="text-muted d-inline-block" style="font-size: 1em;">
                                        <span v-if="item.orderable_type === 'App\\Models\\Service' || item.orderable_type === 'App\\Models\\ProviderItem' || item.orderable_type === 'App\\Models\\ProviderItemSpec'">
                                            <buyable-price :provider="provider" :orderItem="item" :forceCurrency="booking.order.currency" /> x{{item.count}}
                                        </span>
                                        <span v-if="item.returned_at" class="text-danger">{{ $t('returned') }}</span>
                                    </div>
                                </div>
                                <div class="text-right flex-grow-1">{{ getExchangedAmount(item.total, booking.order.currency)}}</div>
                            </div>
                        </div>
                        <span v-else>{{$t('orders.no_booking_order_items')}}</span>
                        <div v-if="booking.order.logistic" class="d-flex">
                            <div>{{$t('logistic_fee')}}</div>
                            <div class="text-right flex-grow-1">{{ getExchangedAmount(booking.order.logistic_total, booking.order.currency) }}</div>
                        </div>
                        <hr class="my-1">
                        <div class="d-flex">
                            <div>{{$t('total')}}</div>
                            <div class="text-right flex-grow-1">{{ getExchangedAmount(booking.order.total, booking.order.currency) }}</div>
                        </div>
                    </div>
                    <div>
                        <label class="field-label">{{$t('campaigns.campaigns')}}</label>
                    </div>
                    <div v-if="campaignOrderItems.length > 0" class="mb-4">
                        <div v-for="item in campaignOrderItems" :key="item.id" class="d-flex">
                            <div>{{ item.title }}</div>
                        </div>
                    </div>
                </div>
                <div v-if="booking && booking.order && booking.order.logistic">
                    <div>
                        <label class="field-label">{{$t('logistic')}}</label>
                    </div>
                    <div class=" mb-4">
                        {{booking.order.logistic.display_name}}
                        <small class="text-muted d-inline-block">{{$t('logistics.type_' + booking.order.logistic.type)}}<span v-if="booking.order.logistic.type !== 'FREE_OR_SELF_TAKEN'"> /
                        {{$t('logistics.condition_' + booking.order.logistic.condition)}}</span></small>

                        <small class="text-muted d-inline-block" v-if="booking.order.logistic.trade_no">
                            / {{ $t('logistics.trade_no') }} {{ booking.order.logistic.trade_no }}
                        </small>

                        <div v-if="booking.order.logistic.address || booking.order.logistic.admin_level_1">
                            {{$t('country.' + booking.order.logistic.country)}}
                            {{booking.order.logistic.admin_level_1}}
                            {{booking.order.logistic.admin_level_2}}
                            {{booking.order.logistic.address}}
                        </div>
                        <div v-if="booking.order.logistic.address || booking.order.logistic.admin_level_1">
                            <span v-if="booking.order.logistic.receiver_name">{{booking.order.logistic.receiver_name}}</span>
                            <span v-else>{{booking.customer.name}}</span>

                            <span v-if="booking.order.logistic.receiver_phone">{{booking.order.logistic.receiver_phone}}</span>
                            <span v-else>+{{booking.customer.phone_calling_code + '-' + booking.customer.phone_number}}</span>
                        </div>
                    </div>
                </div>
                <div class="lower" v-if="nonEmptyAnswers.length > 0">
                    <div class="row">
                        <div class="col-12 pb-2">
                            <div class="form-row question-section" v-for="answer in nonEmptyAnswers">
                                <div class="col-12">
                                    <span class="field-label">
                                        Q. {{answer.question.parsedContent?.meta.title || ''}}
                                    </span>
                                </div>
                                <div class="col-12 pt-2 pl-4">
                                    <!-- Simple text answer -->
                                    <span v-if="!answer.parsedContent || (typeof answer.parsedContent !== 'object')" class="field-value">{{answer.content}}</span>
                                    <!-- Date answer -->
                                    <span v-if="answer.parsedContent && answer.parsedContent.date" class="field-value">{{answer.parsedContent.date}}</span>
                                    <!-- Linear scale answer -->
                                    <span v-if="answer.parsedContent && (answer.parsedContent.scale === 0 || answer.parsedContent.scale)" class="field-value">
                                        {{ answer.parsedContent.scale }}
                                    </span>
                                    <!-- Single/Multiple Choices -->
                                    <div v-for="o in optionsToDisplayForAnswer(answer)" class="option-container">
                                        <img
                                            v-if="o.imageUrl"
                                            :src="o.imageUrl"
                                            class="option-img"
                                        />
                                        <span class="option-text">{{o.text}}</span>
                                        <!-- noteMap text -->
                                        <div v-if="o.note" class="field-note ml-1 mr-1 pl-1 pr-1 text-left"> {{o.note}} </div>
                                    </div>
                                    <!-- Single/Multiple Matrix -->
                                    <div v-for="(m, index) in answerToDisplayForMatrix(answer)" class="option-container" :key="index">
                                        <span class="option-text">{{m.text}}</span>
                                    </div>
                                    <!-- Files -->
                                    <div v-if="answer.parsedContent && answer.parsedContent.files " class="field-note ml-1 mr-1 pl-1 pr-1 text-left">
                                        <div v-for="f in answer.parsedContent.files" class="d-flex row mt-2">
                                            <div :class="{'col-12': f.caption, 'col-auto': !f.cation}">
                                                <div v-if="f.type !== 'pdf'" class="file-container">
                                                    <a class="d-inline-block" :href="context.storageEndpoint + f.path" target="_blank" ><img
                                                        :src="context.storageEndpoint + f.path"
                                                        class="file-img option-img"/></a>
                                                    <span v-if="f.caption" class="option-text file-text pl-2">{{f.caption}}</span>
                                                </div>
                                                <div v-else>
                                                    <a :href="context.storageEndpoint + f.path" target="_blank">{{f.name || 'File'}}</a>
                                                    <span> {{f.caption}} </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="booking" slot="footer" class="p-2 d-flex flex-wrap" style="gap: 8px;">
            <div v-if="supportBookAgain(booking)" class="flex-grow-1">
                <button class="btn btn-block btn-primary btn-sm" @click="bookAgain">{{$t('booking.book_again')}}</button>
            </div>
            <div v-if="shouldShowCancelButton"  class="flex-grow-1">
                <button class="btn btn-block btn-danger btn-sm" @click="handleCancel">{{$t('booking.cancel_booking')}}</button>
            </div>
            <div class="flex-grow-1 w-100">
                <a href="javascript:void(0);" class="btn btn-block btn-sm btn-secondary-outline" @click="$emit('close')">{{$t('general.go_back')}}</a>
            </div>
        </div>
    </modal>
</template>

<script>
import modal from '../common/Modal';
import api from '../../mixins/api';
import displayBooking from '../../mixins/display-booking';
import promptConfirm from '../../mixins/prompt-confirm';
import moment from 'moment';
import AtmReportModal from '../payments/AtmReportModal.vue';
import BuyablePrice from '../items/BuyablePrice.vue';
import { mapGetters } from 'vuex';
import RootStore from '../../stores/root';
import { localizedAmount } from '../../currency';
import bu from '../../bu/api';

export default {
    mixins: [api, promptConfirm, displayBooking], 
    components: { AtmReportModal, BuyablePrice },
    store: RootStore,
    props: {
        provider: {
            required: true,
            type: Object
        },
        context: {
            required: true,
            type: Object
        }
    },
    data() {
        return {
            displayIdToCheck: null,
            error: null,
            booking: null,
            paymentToShowAtmReportModal: null
        }
    },
    computed: {
        ...mapGetters({
            currency: 'currencies/currency',
            currencyRateMap: 'currencies/currencyRateMap',
            currencyOptions: 'currencies/currencyOptions',
        }),
        acceptingPayment() {
            return this.booking && (this.booking.status === 'unapproved' || this.booking.status === 'approved');
        },
        recentPaidPayment() {
            if (this.booking && this.booking.paid_payments) {
                let results = this.booking.paid_payments
                    .sort((a, b) => moment(a.paid_at).diff(moment(b.paid_at)))
                    .filter((payment) => {
                        let diffMinute = moment().diff(moment(payment.paid_at), 'minute');
                        console.log(diffMinute);
                        return (diffMinute < 3);
                    });
                if (results.length > 0) {
                    return results[0]
                }
            }
            return null;
        },
        shouldShowCancelButton() {
            if (this.booking) {
                let now = moment();
                let end = moment(this.booking.end_datetime);
                return now.isBefore(end) && (
                    this.booking.status === 'unapproved' || this.booking.status === 'approved'
                );
            }
            return false;
        },
        customerInformation() {
            let c = this.booking.customer;
            let items = [c.name];
            if (c.phone_calling_code && c.phone_number) {
                items.push('+' + c.phone_calling_code + c.phone_number);
            }
            if (c.email) {
                items.push('<br>' + c.email);
            }
            return items.join('  ')
        },
        nonEmptyAnswers() {
            if (!this.booking.answers) {
                return [];
            }

            return this.booking.answers.filter((answer) => {
                if (answer.parsedContent) {
                    if (answer.parsedContent.choices) {
                        return answer.parsedContent.choices.length > 0;
                    }

                    if (answer.parsedContent.files) {
                        return answer.parsedContent.files.length > 0;
                    }
                }

                if (answer.selected_options && answer.selected_options.length > 0) {
                    return true;
                }

                // Lastly check if it's a multiple selection with notes
                // If it is, then it's empty because there's no selected options
                if (answer.parsedContent && answer.parsedContent.noteMap) {
                    return false;
                }

                return !!answer.content
            })
            .sort((a, b) => {
                return a.question.order - b.question.order;
            });
        },
        hasExpiredPayments() {
            return this.booking.expired_payments && this.booking.expired_payments.length > 0;
        },
        hasPendingPayments() {
            return this.booking.pending_payments && this.booking.pending_payments.length > 0;
        },
        hasCancelledPayments() {
            return this.booking.cancelled_payments && this.booking.cancelled_payments.length > 0;
        },
        nonEmptyOrderItems() {
            return this.booking.order ? this.booking.order.items.filter(item => {
                if (item.orderable_type === 'App\\Models\\Service' || item.orderable_type === 'App\\Models\\ProviderItem' || item.orderable_type === 'App\\Models\\ProviderItemSpec') {
                    return true;
                } else {
                    return item.total !== 0;
                }
            }) : [];
        },
        campaignOrderItems() {
            return this.booking.order ? this.booking.order.items.filter(item => item.orderable_type === 'App\\Models\\ProviderCampaign') : [];
        }
    },
    methods: {
        localizedAmount,
        supportBookAgain(booking) {
            const isUsingNewBookingForm = this.provider.landing_version == 2;
            return booking.status === 'unapproved' || booking.status === 'approved' && isUsingNewBookingForm;
        },
        onPaymentATMReported(payment, atmReport) {
            this.$set(payment, 'atm_payment', atmReport);
            const pendingPayments = this.booking.pending_payments;
            const index = pendingPayments.findIndex((pp) => pp.id == payment.id);
            if (index) {
                this.$set(this.booking.pending_payments[index], 'atm_payment', atmReport);
            }
            this.paymentToShowAtmReportModal = null;
        },
        formatedTransferredAt(atmPayment) {
            return moment(atmPayment.transferred_at).tz(this.provider.timezone).format('YYYY-MM-DD A h:mm')
        },
        paymentValidThrough(payment) {
            if (payment.expire_days) {
                return moment(payment.created_at).add(payment.expire_days, 'day').format('Y-MM-DD A h:mm');
            }
            if (this.provider.payment_expire_days) {
                return moment(payment.created_at).add(this.provider.payment_expire_days, 'day').format('YYYY-MM-DD A h:mm');
            }
            return '';
        },
        optionsToDisplayForAnswer(answer) {
            var answersWithoutMatchingOptions = [];
            var choices = [];
            if (answer.parsedContent && answer.parsedContent.choices) {
                choices = answer.parsedContent.choices;
            } else if (answer.selected_options) {
                // LEGACY: Get all selected choices from `selected_options` which is references-based
                choices = answer.selected_options.map((item) => { return item.option.content_unit.text; });
            }

            // loop through choices and see if there's any with no option matched
            var optionsToDisplay = [];
            choices.forEach((choiceText) => {
                var optionFound = answer.question.options.filter((o) => o.content_unit.text === choiceText);
                var note = null;
                var imageUrl = null;
                if (optionFound.length > 0) {
                    var selectedOption = optionFound[0];

                    imageUrl = selectedOption.content_unit.file_name ? this.context.storageEndpoint + selectedOption.content_unit.file_name : null;
                    if (answer.parsedContent && answer.parsedContent.noteMap) {
                        note = answer.parsedContent.noteMap[choiceText] || answer.parsedContent.noteMap[selectedOption.provider_option_id];
                    }
                } else {
                    if (answer.parsedContent && answer.parsedContent.noteMap) {
                        note = answer.parsedContent.noteMap[choiceText];
                    }
                }
                optionsToDisplay.push({ text: choiceText, imageUrl, note });
            });
            return optionsToDisplay;
        },
        answerToDisplayForMatrix(answer) {
            if (!answer.parsedContent?.matrixChoicesMap) {
                return;
            }

            const choicesMap = answer.parsedContent.matrixChoicesMap;
            const rowList = Object.values(choicesMap);

            var answerToDisplay = [];
            rowList.filter(row => row.length).forEach((row) => {
                const text = row.join(', ');
                answerToDisplay.push({ text });
            });
            return answerToDisplay;
        },
        setDisplayIdToCheck(id) {
            this.displayIdToCheck = id;
            this.getBooking();
        },
        handleCancel() {
            if (this.booking.from_booking_pass_preset) {
                this.promptAlert(this.$t('customer_booking_checker.booking_pass_cannot_cancel'));
                return;
            }

            if (this.booking.status === 'approved') {
                if (this.booking.service.cancellation_policy === 'NO') {
                    let text = this.booking.service.cancellation_text || this.$t('customer_booking_checker.default_no_cancellation', {phone: this.provider.phone, email: this.provider.email})
                    this.promptAlert(text);
                    return;
                }

                if (this.booking.service.cancellation_policy === 'BEFORE_N' && !this.isNowBeforeNOfBooking()) {
                    let text = this.booking.service.cancellation_text || this.$t('customer_booking_checker.default_no_cancellation', {phone: this.provider.phone, email: this.provider.email})
                    this.promptAlert(text);
                    return;
                }

                if (this.booking.service.cancellation_policy === 'ANYTIME' && moment().isAfter(moment(this.booking.start_datetime))) {
                    let text = this.booking.service.cancellation_text || this.$t('customer_booking_checker.default_no_cancellation_anytime_pass_start_datetime');
                    this.promptAlert(text);
                    return;
                }

                if (this.booking.service.cancellation_policy === 'ANYTIME_BEFORE_END' && moment().isAfter(moment(this.booking.end_datetime))) {
                    let text = this.$t('customer_booking_checker.default_no_cancellation_anytime_pass_end_datetime');
                    this.promptAlert(text);
                    return;
                }
            }

            const messageKey = this.booking.from_booking_pass_preset ?
                'customer_booking_checker.sure_to_cancel_booking_with_presets' : 'customer_booking_checker.sure_to_cancel_booking';

            this.promptConfirm(
                this.$t(messageKey),
                this.$t('customer_booking_checker.cancel_booking'),
                () => {
                    let hideLoading = this.promptLoading();
                    this.customerCancelBooking(this.booking.display_id, (result, error, errors, message) => {
                        hideLoading();
                        if (error) {
                            if (message === 'contract already signed') {
                                this.promptAlert(this.$t('customer_booking_checker.contract_already_signed'));
                            } else {
                                this.promptAlert(error);
                            }
                        } else {
                            this.promptAlert(this.$t('customer_booking_checker.booking_cancelled'), () => {
                                this.$emit('close');
                            }, 'success');
                        }
                    });
                },
                'btn btn-sm btn-danger',
                this.$t('general.go_back')
            );

        },
        isNowBeforeNOfBooking() {
            let now = moment();
            let deadline = moment(this.booking.start_datetime).subtract(
                this.booking.service.cancellation_before_n_value,
                this.booking.service.cancellation_before_n_unit,
            );
            console.log([now, deadline]);
            return now.isBefore(deadline);
        },
        getBooking() {
            if (!this.displayIdToCheck) {
                return;
            }

            let hideLoading = this.promptLoading();
            this.getBookingWithDisplayId(this.displayIdToCheck, (booking) => {
                hideLoading();
                if (!booking) {
                    this.error = this.$t('customer_booking_checker.no_booking_found');
                } else {
                    this.processBookings(this.provider, [booking]);
                    this.booking = booking;
                }
            });
        },
        isBookingOverdue(booking) {
            return moment(booking.start_datetime).isBefore(moment());
        },
        getExchangedAmount(amount, currency) {
            return this.localizedAmount(amount, currency, this.currencyOptions);
        },
        async bookAgain() {
            const closeLoading = this.promptLoading(this.$t('message.processing'));
            try {
                // Verify service and member user
                const [serviceResponse, membersResponse] = await Promise.all([
                    bu.getService(this.booking.service.id),
                    bu.getAllMembers()
                ]);
                
                const service = serviceResponse.data;
                const members = membersResponse.data;
                
                if (!service || service.is_disabled) {
                    this.promptAlert(this.$t('message.service_not_available'));
                    return;
                }
                
                const member = members.find(m => m.id === this.booking.member_user.id);
                if (!member || member.deleted_at) {
                    this.promptAlert(this.$t('message.member_not_available'));
                    return;
                }

                // Save order items to localStorage with expiration
                const orderItems = this.booking.order?.items?.filter(
						orderItem => orderItem.orderable_type == 'App\\Models\\ProviderItem'
							|| orderItem.orderable_type == 'App\\Models\\ProviderItemSpec'
							|| orderItem.orderable_type == 'App\\Models\\BookingPass'
				).map(orderItem => {
						const isItem = orderItem.orderable_type === 'App\\Models\\ProviderItem';
						const isSpec = orderItem.orderable_type === 'App\\Models\\ProviderItemSpec';
						const isBookingPass = orderItem.orderable_type === 'App\\Models\\BookingPass';
						return {
							provider_item_id: isItem ? orderItem.orderable_id : null,
							provider_item_spec_id: isSpec ? orderItem.orderable_id : null,
							booking_pass_id: isBookingPass ? orderItem.orderable_id : null,
							count: orderItem.count
						};
					});

                const pendingOrder = {
                    items: orderItems,
                    expiresAt: Date.now() + 60000 // 1 minute expiration
                };

                localStorage.setItem('pendingOrder', JSON.stringify(pendingOrder));
                
                window.location.href = `/reserve?sid=${this.booking.service.id}&mid=${this.booking.member_user.id}`;
            } catch (error) {
                console.error(error);
                this.promptAlert(this.$t('message.fail_to_book_again'));
            } finally {
                closeLoading();
            }
        },
    }
}
</script>

<style scoped lang="scss">
.modal-content-wrapper {
    padding-bottom: 0px !important;
}
    .field-label {
        font-size: 0.8em;
        color: #90A4AE;
        word-break: break-all;
        width: 100%;
    }

    .field-value {
        word-break: break-all;
        font-size: 1.1em;
        color: #31393C;
        margin-bottom: 14px;
    }

    .lower {
        background-color: #f7f7f7;
        padding: 15px;
        padding-bottom: 30px;
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-orient: horizontal;
        -webkit-box-direction: normal;
        -ms-flex-direction: row;
        flex-direction: row;
        overflow-y: scroll;
        max-height: 400px;
        margin-left: -15px;
        margin-right: -15px;
    }

    .option-img {
        width: 120px;
    }

    .option-container {
        display: inline-block;
        background: #D8d8d8;
        padding: 4px;
        border-radius: 2px;
        color: #555;
        text-align: center;
        margin-right: 8px;

        img {
            display: block;
        }

        .field-note {
            background: #e8e8e8;
            white-space: pre-line;
        }
    }

    .option-text {
        display: inline-block;
        word-break: break-all;
    }

    .icon-payment {
        display: inline-block;
        position: relative;
        top: 4px;
        margin-right: 12px;
        width: 18px;
        height: 18px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center;
        background-image: url('/img/icon_payment_tinted.png');
    }

    .white-space-pre-wrap {
        white-space: pre-wrap;
    }
</style>

<i18n>
{
    "zh": {
        "pay": "點此線上付款",
        "payment_requests": "待付款項",
        "paid_payments": "已付款",
        "you_just_paid": "謝謝您！付款已完成！",
        "pay_via": "{name}",
        "expired": "已逾期/無法付款",
        "cancelled": "已取消/無法付款",
        "payment_valid_through": "{validThrough} 前需付款",
        "order": "訂單內容",
        "logistic_fee": "物流費用",
        "total": "總計",
        "logistic": "物流配送",
        "manual_collect": "線下收款",
        "report_bank_transfer": "回傳轉帳紀錄",
        "check_atm_report_photo": "查看照片",
        "reported_last_5_digits": "已回報後五碼",
        "transferred_at": "轉帳時間",
        "returned": "已退還",
        "points_deduction": "會員點數折抵 ({points} 點)",
		"message": {
			"processing": "處理中...",
			"service_not_available": "此服務已不再提供，無法再次預約",
			"member_not_available": "此服務人員已不再提供服務，無法再次預約",
			"fail_to_book_again": "再次預約失敗，請稍後再試。"
		}
    },
    "en": {
        "pay": "Click to Pay",
        "payment_requests": "Pending Payments",
        "paid_payments": "Paid",
        "you_just_paid": "Payment received. Thanks!",
        "pay_via": "{name}",
        "expired": "Expired",
        "cancelled": "Cancelled",
        "payment_valid_through": "Pay before {validThrough} required.",
        "order": "Order",
        "logistic_fee": "Delivery Fee",
        "total": "Total",
        "logistic": "Delivery",
        "manual_collect": "Pay Offline",
        "report_bank_transfer": "Report Transfer Info",
        "check_atm_report_photo": "Check Report Photo",
        "last_5_digits": "Reported last 5 Digits",
        "transferred_at": "Transferred At",
        "returned": "Returned",
        "points_deduction": "Member points deduction ({points} points)",
		"message": {
			"processing": "Processing...",
			"service_not_available": "This service is no longer available",
			"member_not_available": "This staff member is no longer available",
			"fail_to_book_again": "Failed to book again. Please try again later."
		}
    }
}
</i18n>